import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import type { BlogPublicCategoryNodeDto } from '@/api';
import { SidebarNavItemComponent } from '@/components/sidebar-nav/sidebar-nav-item/sidebar-nav-item.component';
import { APP_ROUTES } from '@/routes/routes.type';
import { BlogNavStore } from '@/store/blog-nav.store';
import { SidebarUiStore } from '@/store/sidebar-ui.store';
import { findBlogCategoryAncestors } from '@/utils/blog-category-tree';
import { BlogSidebarNavTreeComponent } from './blog-sidebar-nav-tree.component';

@Component({
  selector: 'app-blog-sidebar-nav',
  standalone: true,
  imports: [SidebarNavItemComponent, BlogSidebarNavTreeComponent],
  templateUrl: './blog-sidebar-nav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogSidebarNavComponent {
  readonly #router = inject(Router);
  readonly #blogNavStore = inject(BlogNavStore);
  readonly #sidebarUiStore = inject(SidebarUiStore);

  readonly roots = this.#blogNavStore.roots;
  readonly loading = this.#blogNavStore.loading;

  readonly #manualExpanded = signal<ReadonlySet<string>>(new Set());

  readonly activeCategorySlug = toSignal(
    this.#router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.#extractCategorySlug()),
      startWith(this.#extractCategorySlug()),
    ),
    { initialValue: this.#extractCategorySlug() },
  );

  readonly expandedSlugs = computed(() => {
    const roots = this.roots();
    const active = this.activeCategorySlug();
    const expanded = new Set(this.#manualExpanded());

    if (active) {
      for (const slug of findBlogCategoryAncestors(active, roots)) {
        expanded.add(slug);
      }
      if (this.#nodeHasChildren(active, roots)) {
        expanded.add(active);
      }
    }

    return expanded;
  });

  closeSidebar(): void {
    this.#sidebarUiStore.close();
  }

  onToggleExpand(slug: string): void {
    this.#manualExpanded.update((current) => {
      const next = new Set(current);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }

  #extractCategorySlug(): string | null {
    const path = this.#router.url.split('?')[0];
    const prefix = `/${APP_ROUTES.BLOG}/c/`;
    if (!path.startsWith(prefix)) {
      return null;
    }
    const rest = path.slice(prefix.length);
    const slug = rest.split('/')[0];
    return slug || null;
  }

  #nodeHasChildren(slug: string, roots: BlogPublicCategoryNodeDto[]): boolean {
    const find = (nodes: BlogPublicCategoryNodeDto[]): BlogPublicCategoryNodeDto | null => {
      for (const node of nodes) {
        if (node.slug === slug) {
          return node;
        }
        const inChild = find(node.children);
        if (inChild) {
          return inChild;
        }
      }
      return null;
    };
    return (find(roots)?.children.length ?? 0) > 0;
  }
}
